/**
 * CompetitionList - the competitions the match wizard offers.
 *
 * Backed by /api/competitions/ (operator migration 0108). Every row carries
 * the label the {competition} token renders, the three-letter code in the
 * event title, the calendar colour and how many matches are filed under it.
 *
 * - Add / edit: managers and planners.
 * - Deactivate: hides a row from the wizard, existing matches keep resolving.
 * - Delete: only when no match references the slug; otherwise the server
 *   answers 409 with the count and that message is shown as-is.
 * - Every write invalidates the shared competitions query, so the wizard's
 *   dropdown, cards and calendar pick the change up without a reload.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import {
  fetchCompetitions,
  createCompetition,
  updateCompetition,
  deleteCompetition,
} from '../../../../api';
import Button from '../../../../shared/components/ui/Button';
import Modal from '../../../../shared/components/ui/Modal';
import Spinner from '../../../../shared/components/ui/Spinner';
import { Btn, Drawer } from '../../../../shared/components/redesign';
import TagInput from '../../../../shared/components/ui/TagInput/TagInput';
import { useInvalidateCompetitions } from '../../../matches/hooks/useCompetitions';
import styles from './CompetitionList.module.css';

const COUNTRY_OPTIONS = [
  ['', 'None'],
  ['NL', 'Netherlands'],
  ['BE', 'Belgium'],
  ['GB', 'United Kingdom'],
  ['DE', 'Germany'],
  ['ES', 'Spain'],
  ['FR', 'France'],
  ['PT', 'Portugal'],
  ['IT', 'Italy'],
  ['EU', 'Europe (UEFA)'],
  ['INT', 'International'],
];

const slugify = (label) =>
  String(label || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);

function errorText(err) {
  const data = err?.response?.data;
  if (!data) return err?.message || 'Request failed';
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  // DRF field errors: { field: ["msg"] }
  return Object.entries(data)
    .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(' ') : msgs}`)
    .join(' · ');
}

function CompetitionDrawer({ competition, isNew, onSave, onClose }) {
  const [form, setForm] = useState({
    slug: competition?.slug || '',
    label: competition?.label || '',
    aliases: competition?.aliases || [],
    short_code: competition?.short_code || '',
    color: competition?.color || '#a0a0a0',
    country: competition?.country || '',
    sort_order: competition?.sort_order ?? 100,
    active: competition?.active ?? true,
  });
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const onLabelChange = (label) => {
    setForm((prev) => ({
      ...prev,
      label,
      slug: isNew && !slugTouched ? slugify(label) : prev.slug,
    }));
  };

  const codeOk = /^[A-Za-z0-9]{3}$/.test(form.short_code);
  const colorOk = /^#[0-9a-fA-F]{6}$/.test(form.color);
  const canSave = form.label.trim() && form.slug.trim() && codeOk && colorOk && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        label: form.label.trim(),
        aliases: form.aliases,
        short_code: form.short_code.toUpperCase(),
        color: form.color.toLowerCase(),
        country: form.country,
        sort_order: Number(form.sort_order) || 100,
        active: !!form.active,
      };
      if (isNew) payload.slug = form.slug.trim();
      await onSave(payload);
      onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const body = (
    <>
      {error && (
        <div className={styles.error}>
          <span>{error}</span>
        </div>
      )}
      <div className={styles.formGroup}>
        <label className={styles.label}>Label</label>
        <input
          type="text"
          className={styles.input}
          value={form.label}
          onChange={(e) => onLabelChange(e.target.value)}
          placeholder="e.g. UEFA Youth League"
          autoFocus
        />
        <span className={styles.hint}>
          Rendered by the {'{competition}'} token in headlines, captions and keywords.
        </span>
      </div>
      <div className={styles.formGroup}>
        <TagInput
          label="Keyword aliases"
          value={form.aliases}
          onChange={(aliases) => set('aliases', aliases)}
          placeholder="e.g. Dutch Eredivisie"
        />
        <span className={styles.hint}>
          Extra keywords written right after the label, on every destination. Keywords only: the{' '}
          {'{competition}'} token keeps rendering the label.
        </span>
      </div>
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Slug</label>
          <input
            type="text"
            className={styles.input}
            value={form.slug}
            disabled={!isNew}
            onChange={(e) => {
              setSlugTouched(true);
              set('slug', slugify(e.target.value));
            }}
            placeholder="uefa_youth_league"
          />
          <span className={styles.hint}>
            {isNew ? 'Stored on every match; cannot change later.' : 'Fixed: matches are filed under it.'}
          </span>
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>Title code</label>
          <input
            type="text"
            className={styles.input}
            value={form.short_code}
            maxLength={3}
            onChange={(e) => set('short_code', e.target.value.toUpperCase())}
            placeholder="UYL"
          />
          {form.short_code && !codeOk ? (
            <span className={styles.fieldError}>Exactly three letters or digits.</span>
          ) : (
            <span className={styles.hint}>Third segment of HOME_AWAY_CODE_DDMMYY.</span>
          )}
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Colour</label>
          <div className={styles.colorRow}>
            <input
              type="color"
              value={colorOk ? form.color : '#a0a0a0'}
              onChange={(e) => set('color', e.target.value)}
              aria-label="Pick colour"
            />
            <input
              type="text"
              className={styles.input}
              value={form.color}
              onChange={(e) => set('color', e.target.value)}
              placeholder="#ff6b6b"
            />
          </div>
          {form.color && !colorOk && <span className={styles.fieldError}>Hex colour like #ff6b6b.</span>}
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>Country</label>
          <select
            className={styles.input}
            value={form.country}
            onChange={(e) => set('country', e.target.value)}
          >
            {COUNTRY_OPTIONS.map(([value, label]) => (
              <option key={value || 'none'} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Sort order</label>
          <input
            type="number"
            className={styles.input}
            value={form.sort_order}
            min={0}
            onChange={(e) => set('sort_order', e.target.value)}
          />
          <span className={styles.hint}>Lower sorts first in the wizard.</span>
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>Visibility</label>
          <label className={styles.checkboxItem}>
            <input
              type="checkbox"
              checked={!!form.active}
              onChange={(e) => set('active', e.target.checked)}
            />
            <span>Offered in the match wizard</span>
          </label>
          <span className={styles.hint}>Off: hidden from new matches, existing ones keep it.</span>
        </div>
      </div>
    </>
  );

  return (
    <Drawer
      open
      onClose={onClose}
      eyebrow={isNew ? '§ COMPETITION · NEW' : '§ COMPETITION'}
      title={isNew ? 'New competition' : competition?.label || 'Competition'}
      ariaLabel={isNew ? 'Create competition' : 'Edit competition'}
      body={body}
      footer={
        <>
          <Btn kind="ghost" size="sm" onClick={onClose}>
            Cancel
          </Btn>
          <span className={styles.footerSpacer} />
          <Btn kind="primary" size="sm" onClick={handleSave} disabled={!canSave}>
            {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
          </Btn>
        </>
      }
    />
  );
}

export default function CompetitionList() {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(true);

  const [drawer, setDrawer] = useState(null); // { competition, isNew }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const invalidate = useInvalidateCompetitions();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchCompetitions();
      setCompetitions(Array.isArray(rows) ? rows : rows.results || []);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return competitions.filter((c) => {
      if (!showInactive && !c.active) return false;
      if (!term) return true;
      return (
        c.label.toLowerCase().includes(term) ||
        c.slug.includes(term) ||
        c.short_code.toLowerCase().includes(term) ||
        (c.aliases || []).some((alias) => alias.toLowerCase().includes(term))
      );
    });
  }, [competitions, search, showInactive]);

  const afterWrite = async (message) => {
    await load();
    invalidate();
    setNotice(message);
  };

  const handleSave = async (payload) => {
    if (drawer?.isNew) {
      const row = await createCompetition(payload);
      await afterWrite(`${row.label} (${row.short_code}) added. It is in the match wizard now.`);
    } else {
      const row = await updateCompetition(drawer.competition.id, payload);
      await afterWrite(`${row.label} saved.`);
    }
  };

  const toggleActive = async (row) => {
    setError(null);
    try {
      const saved = await updateCompetition(row.id, { active: !row.active });
      await afterWrite(
        saved.active
          ? `${saved.label} is offered in the match wizard again.`
          : `${saved.label} is hidden from the match wizard. ${
              saved.match_count
                ? `${saved.match_count} existing match${saved.match_count === 1 ? '' : 'es'} keep it.`
                : ''
            }`.trim(),
      );
    } catch (err) {
      setError(errorText(err));
    }
  };

  const requestDelete = (row) => {
    setError(null);
    if (row.match_count > 0) {
      // Same answer the server gives; no round trip needed to know it.
      setError(
        `${row.label} still has ${row.match_count} match${row.match_count === 1 ? '' : 'es'} filed under it. ` +
          'Deactivate it to hide it from the wizard, or move those matches first.',
      );
      return;
    }
    setConfirmDelete(row);
  };

  const confirmDeleteAction = async () => {
    const row = confirmDelete;
    setConfirmDelete(null);
    try {
      await deleteCompetition(row.id);
      await afterWrite(`${row.label} deleted.`);
    } catch (err) {
      // 409: matches were filed under it since the list loaded.
      setError(errorText(err));
      load();
    }
  };

  if (loading && competitions.length === 0) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading competitions...</p>
      </div>
    );
  }

  const activeCount = competitions.filter((c) => c.active).length;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Competitions</h2>
          <p className={styles.subtitle}>
            What the match wizard offers, and what the {'{competition}'} token and the title
            code render into.
          </p>
        </div>
        <Button variant="primary" onClick={() => setDrawer({ competition: null, isNew: true })}>
          + New Competition
        </Button>
      </div>

      {error && (
        <div className={styles.error} role="alert">
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      {notice && (
        <div className={styles.notice} role="status">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)}>OK</button>
        </div>
      )}

      <div className={styles.filters}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search label, alias, slug or code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className={styles.checkboxItem}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          <span>Show hidden</span>
        </label>
        <span className={styles.count}>
          {visible.length} of {competitions.length} · {activeCount} in the wizard
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Competition</th>
              <th>Slug</th>
              <th>Code</th>
              <th>Country</th>
              <th style={{ textAlign: 'right' }}>Matches</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.id} className={clsx(!row.active && styles.inactiveRow)}>
                <td>
                  <span className={styles.swatch} style={{ background: row.color }} />
                  {row.label}
                  {!row.active && <span className={styles.badge}>Hidden</span>}
                </td>
                <td className={styles.slug}>{row.slug}</td>
                <td className={styles.code}>{row.short_code}</td>
                <td>{row.country || '—'}</td>
                <td style={{ textAlign: 'right' }}>{row.match_count ?? 0}</td>
                <td>
                  <div className={styles.actions}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDrawer({ competition: row, isNew: false })}
                    >
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleActive(row)}>
                      {row.active ? 'Hide' : 'Show'}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => requestDelete(row)}
                      title={
                        row.match_count > 0
                          ? `${row.match_count} matches are filed under it; deactivate instead`
                          : 'Delete'
                      }
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan={6} className={styles.empty}>
                  {competitions.length === 0
                    ? 'No competitions yet. Add one to offer it in the match wizard.'
                    : 'No competition matches the search.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {drawer && (
        <CompetitionDrawer
          competition={drawer.competition}
          isNew={drawer.isNew}
          onSave={handleSave}
          onClose={() => setDrawer(null)}
        />
      )}

      <Modal isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)} size="sm">
        <Modal.Header onClose={() => setConfirmDelete(null)}>Delete competition</Modal.Header>
        <Modal.Body>
          <p>
            Delete "{confirmDelete?.label}" ({confirmDelete?.short_code})?
          </p>
          <p style={{ marginTop: 8, fontSize: 13, color: '#999' }}>
            No match is filed under it. This cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteAction}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
